import type { FC, ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';

type GridItem = {
  /**
   * 表示するコンポーネント
   */
  field: ReactNode;

  /**
   * デスクトップ時のグリッド列スパン（デフォルト: 1）
   * アイテムが占有するカラム数を指定
   */
  cols?: 1 | 2 | 3 | 4;

  /**
   * 一意なキー
   */
  key?: string;
};

type GridProps = {
  /**
   * グリッドアイテムの配列（行ごとにグループ化）
   */
  items: GridItem[][];

  /**
   * デスクトップ時の基準カラム数（デフォルト: 4）
   */
  columns?: 1 | 2 | 3 | 4;

  /**
   * グリッド間の間隔（デフォルト: 6）
   */
  gap?: 4 | 6 | 8;

  /**
   * 追加のクラス名
   */
  className?: string;
};

const GAP_CLASSES = {
  4: 'gap-4',
  6: 'gap-6',
  8: 'gap-8',
} as const;

const COLUMN_CLASSES = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
} as const;

// タブレット（sm:）でも列スパンを適用するために sm: と lg: 両方を指定
const COL_SPAN_CLASSES = {
  1: 'sm:col-span-1 lg:col-span-1',
  2: 'sm:col-span-2 lg:col-span-2',
  3: 'sm:col-span-2 lg:col-span-3', // タブレットは最大2カラムなので2を指定
  4: 'sm:col-span-2 lg:col-span-4', // タブレットは最大2カラムなので2を指定
} as const;

/**
 * レスポンシブグリッドレイアウトコンポーネント
 *
 * 画面幅に応じて自動的にカラム数を調整します：
 * - モバイル（<640px）: 1カラム
 * - タブレット（640px-1024px）: 2カラム
 * - デスクトップ（>1024px）: 指定されたカラム数（デフォルト4）
 *
 * @example
 * ```tsx
 * <Grid
 *   items={[
 *     [
 *       { key: 'field1', field: <InputField name="field1" label="フィールド1" /> },
 *       { key: 'field2', field: <InputField name="field2" label="フィールド2" /> },
 *     ],
 *     [
 *       { key: 'wide', field: <TextareaField name="wide" label="幅広" />, cols: 2 },
 *     ],
 *   ]}
 * />
 * ```
 */
export const Grid: FC<GridProps> = ({ items, columns = 4, gap = 6, className }) => {
  return (
    <div className={cn('flex flex-col', GAP_CLASSES[gap])}>
      {items.map((fields, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: 使用しているインデックスは配列の順序が固定されているため安全
          key={index}
          className={cn(
            'grid',
            'grid-cols-1', // モバイル: 1カラム
            'sm:grid-cols-2', // タブレット: 2カラム
            COLUMN_CLASSES[columns], // デスクトップ: 指定されたカラム数
            GAP_CLASSES[gap],
            className,
          )}
        >
          {fields.map((item) => (
            <div key={item.key} className={item.cols ? COL_SPAN_CLASSES[item.cols] : undefined}>
              {item.field}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
